import { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '../lib/api';
import { AuthProvider } from './AuthContext';
import { CartProvider } from './CartContext';
import { PreferencesProvider } from './PreferencesContext';
import { WaitlistProvider } from './WaitlistContext';

/**
 * AppProviders - Composes all context providers in the correct order.
 *
 * Provider order matters:
 * 1. AuthProvider - Must be outermost as other providers depend on user authentication
 * 2. PreferencesProvider - Independent of auth but needed by other features
 * 3. CartProvider - Needs AuthProvider for user-specific cart storage
 * 4. WaitlistProvider - Needs AuthProvider for user-specific waitlist storage
 *
 * Usage:
 * ```tsx
 * // In main.tsx or App.tsx
 * <AppProviders>
 *   <App />
 * </AppProviders>
 * ```
 */
interface AppProvidersProps {
  children: ReactNode;
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PreferencesProvider>
          <CartProvider>
            <WaitlistProvider>{children}</WaitlistProvider>
          </CartProvider>
        </PreferencesProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default AppProviders;
